import React, { useMemo, useEffect, useState } from 'react';
import { useTable, usePagination, useSortBy } from 'react-table';
import { useHistory, useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import CreatePayrollModal from './CreatePayrollModal';
import EditPayrollModal from './EditPayrollModal';
import axios from '../../../../services/AxiosInstance';
import moment from 'moment';
import 'moment/locale/sv';
import { getApiUrl } from '../../../../utils/ApiUtils';
import PageHeader from '../../../layouts/PageHeader';
import { ListToolbar, FilterChips } from '../../Common/ListToolbar';
import EmptyState from '../../Common/EmptyState';
import Callout from '../../Common/Callout';
import TablePager from '../../Common/TablePager';
import { useListControls } from '../../../../hooks/useListControls';
import { useUrlState } from '../../../../hooks/useUrlState';
import { CalendarDays, Edit3, Plus, Trash2 } from 'lucide-react';
import Swal from 'sweetalert2';
import { toast } from 'sonner';

moment.locale('sv');

const monthName = (month) => {
  const name = moment().month(month - 1).format('MMMM');
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const Payrolls = ({ commercialReadOnly = false }) => {
  const { companyId } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search and the year chip live in the URL, like the employee list's
  // controls, so a narrowed view survives a refresh and can be linked.
  const { search, setSearch, debouncedSearch } = useListControls({ defaultSort: null });
  const [yearFilter, setYearFilter] = useUrlState('ar', 'alla');

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleShowModal = () => !commercialReadOnly && setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const handleShowEditModal = (payroll) => {
    if (commercialReadOnly) return;
    setSelectedPayroll(payroll);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedPayroll(null);
  };

  const handleSaveDate = async (data) => {
    if (commercialReadOnly) return;
    try {
      const getPayrollApiUrl = getApiUrl(`${companyId}/payroll`);

      await axios.post(getPayrollApiUrl, {
        year: data.year,
        month: data.month,
        payoutDate: data.payoutDate
      }, { withCredentials: true });

      await fetchPayrollData();
      handleCloseModal();
    } catch (error) {
      setError('Det gick inte att skapa lönekörningen');
    }
  };

  const handleUpdatePayoutDate = async (date) => {
    if (commercialReadOnly) return;
    try {
      if (!selectedPayroll) return;

      const updatePayrollApiUrl = getApiUrl(`${companyId}/payroll/${selectedPayroll.id}/payout-date`);

      const response = await axios.put(updatePayrollApiUrl, {
        payoutDate: date
      }, { withCredentials: true });

      if (response.data.status === 'success') {
        await fetchPayrollData();
        handleCloseEditModal();
      } else {
        setError(response.data.message || 'Det gick inte att uppdatera utbetalningsdatumet');
      }
    } catch (error) {
      console.error('Failed to update payout date:', error);
      const errorMessage = error.response?.data?.message || 'Det gick inte att uppdatera utbetalningsdatumet';
      setError(errorMessage);
    }
  };

  // A period is deletable only while nothing has happened in it: no
  // payslips, not locked, no step flags. The backend enforces the same rule.
  const canDeletePayroll = (p) =>
    (p.numPayslips || 0) === 0 && !p.isLocked && !p.verificationCreated && !p.agiSubmitted && !p.taxesPaid && !p.withdrawalBooked;

  const handleDeletePayroll = async (payroll) => {
    if (commercialReadOnly || !canDeletePayroll(payroll)) return;
    const confirm = await Swal.fire({
      title: 'Ta bort löneperioden?',
      text: `${monthName(payroll.month)} ${payroll.year} tas bort. Det går inte att ångra.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ta bort',
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#dc3545',
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;
    try {
      await axios.delete(getApiUrl(`${companyId}/payroll/${payroll.id}`), { withCredentials: true });
      toast.success('Löneperioden togs bort');
      await fetchPayrollData();
    } catch (error) {
      setError(error.response?.data?.message || 'Det gick inte att ta bort löneperioden');
    }
  };

  const fetchPayrollData = async () => {
    try {
      setLoading(true);
      const getPayrollsApiUrl = getApiUrl(`${companyId}/payrolls`);
      const response = await axios.get(getPayrollsApiUrl, { withCredentials: true });
      setPayrollData(response.data);
    } catch (error) {
      setError('Det gick inte att hämta lönekörningar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayrollData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Year chips: every year that has a period, newest first, with counts.
  const yearChips = useMemo(() => {
    const counts = payrollData.reduce((acc, p) => {
      acc[p.year] = (acc[p.year] || 0) + 1;
      return acc;
    }, {});
    const years = Object.keys(counts).sort((a, b) => b - a);
    return [
      { key: 'alla', label: 'Alla', count: payrollData.length },
      ...years.map((y) => ({ key: String(y), label: String(y), count: counts[y] })),
    ];
  }, [payrollData]);

  const filteredData = useMemo(() => {
    const term = debouncedSearch.toLowerCase();
    return payrollData.filter((payroll) => {
      if (yearFilter !== 'alla' && String(payroll.year) !== yearFilter) return false;
      if (!term) return true;
      const periodText = `${monthName(payroll.month)} ${payroll.year}`.toLowerCase();
      return periodText.includes(term) || String(payroll.year).includes(term);
    });
  }, [payrollData, debouncedSearch, yearFilter]);

  const isFiltered = Boolean(debouncedSearch) || yearFilter !== 'alla';

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5 text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Laddar löneperioder…
        </div>
      );
    }

    if (payrollData.length === 0) {
      return (
        <EmptyState
          icon={CalendarDays}
          title="Inga löneperioder än"
          description="Skapa din första lönekörning för att komma igång med lönehanteringen."
          actions={
            <button type="button" className="ll-btn ll-btn--primary" disabled={commercialReadOnly} onClick={handleShowModal}>
              <Plus />
              Skapa första lönekörningen
            </button>
          }
        />
      );
    }

    if (filteredData.length === 0) {
      return <EmptyState variant="no-matches" query={debouncedSearch} />;
    }

    return (
      <PayrollsTable
        data={filteredData}
        unfilteredTotal={isFiltered ? payrollData.length : undefined}
        onEditPayoutDate={commercialReadOnly ? undefined : handleShowEditModal}
        onDeletePayroll={commercialReadOnly ? undefined : handleDeletePayroll}
        canDeletePayroll={canDeletePayroll}
      />
    );
  };

  return (
    <>
      <PageHeader
        icon={CalendarDays}
        title="Löneperioder"
        subtitle="Hantera löneperioder och utbetalningsdatum för ditt företag."
        actions={
          <button
            type="button"
            className="ll-btn ll-btn--primary"
            onClick={handleShowModal}
            disabled={commercialReadOnly || loading}
          >
            <Plus />
            Ny lönekörning
          </button>
        }
      />

      {error && <Callout tone="danger" role="alert">{error}</Callout>}

      {payrollData.length > 0 && (
        <>
          <ListToolbar
            className="mb-3"
            search={{
              value: search,
              onChange: setSearch,
              placeholder: 'Sök period, år eller månad…',
              ariaLabel: 'Sök bland löneperioder',
            }}
          />
          {yearChips.length > 2 && (
            <FilterChips className="mb-3" chips={yearChips} value={yearFilter} onChange={setYearFilter} />
          )}
        </>
      )}

      {renderContent()}

      <CreatePayrollModal
        show={showModal}
        handleClose={handleCloseModal}
        handleSave={handleSaveDate}
      />

      <EditPayrollModal
        show={showEditModal}
        handleClose={handleCloseEditModal}
        handleSave={handleUpdatePayoutDate}
        initialDate={selectedPayroll?.payoutDate}
        title="Ändra utbetalningsdatum"
        payrollPeriod={selectedPayroll ? { year: selectedPayroll.year, month: selectedPayroll.month } : null}
      />
    </>
  );
};

const PayrollsTable = ({ data, unfilteredTotal, onEditPayoutDate, onDeletePayroll, canDeletePayroll }) => {
  const { companyId } = useParams();
  const history = useHistory();

  const columns = useMemo(() => [
    {
      Header: 'Period',
      accessor: 'perioden',
      Cell: ({ row }) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1f2937' }}>{monthName(row.original.month)}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{row.original.year}</div>
        </div>
      ),
      sortType: (rowA, rowB) => {
        const dateA = new Date(rowA.original.year, rowA.original.month - 1);
        const dateB = new Date(rowB.original.year, rowB.original.month - 1);
        return dateA - dateB;
      }
    },
    {
      Header: 'Skatt senast',
      accessor: 'taxAtLatest',
      colWidth: '11rem',
      Cell: ({ value }) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{moment(value).format('YYYY-MM-DD')}</span>
    },
    {
      Header: 'Utbetalningsdatum',
      accessor: 'payoutDate',
      colWidth: '12rem',
      Cell: ({ value }) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{moment(value).format('YYYY-MM-DD')}</span>
    },
    {
      Header: 'Lönespecar',
      accessor: 'numPayslips',
      className: 'll-num',
      colWidth: '7rem',
      Cell: ({ value }) => <span style={{ color: value > 0 ? '#1f2937' : '#9ca3af' }}>{value}</span>
    },
    {
      Header: '',
      accessor: 'actions',
      className: 'll-table__actions',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="ll-row-actions" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            disabled={!onEditPayoutDate}
            onClick={() => onEditPayoutDate?.(row.original)}
            title="Ändra utbetalningsdatum"
            aria-label="Ändra utbetalningsdatum"
          >
            <Edit3 size={14} />
          </button>
          <button
            type="button"
            className="is-danger"
            disabled={!onDeletePayroll || !canDeletePayroll?.(row.original)}
            onClick={() => onDeletePayroll?.(row.original)}
            title={canDeletePayroll?.(row.original) ? 'Ta bort perioden' : 'Kan bara tas bort när perioden är tom och inget steg är gjort'}
            aria-label="Ta bort perioden"
          >
            <Trash2 size={14} />
          </button>
        </div>
      )
    }
  ], [onEditPayoutDate, onDeletePayroll, canDeletePayroll]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        sortBy: [{ id: 'perioden', desc: true }]
      },
      disableMultiSort: true,
      disableSortRemove: true,
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="ll-table-card">
      <div className="table-responsive">
        <table {...getTableProps()} className="ll-table ll-table--clickable">
          <thead>
            {headerGroups.map((headerGroup, gi) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={`hg-${gi}`}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    key={column.id}
                    className={column.className || ''}
                    style={{ cursor: column.canSort ? 'pointer' : 'default', width: column.colWidth }}
                  >
                    <div className={`d-flex align-items-center gap-1${column.className === 'll-num' ? ' justify-content-end' : ''}`}>
                      <span>{column.render('Header')}</span>
                      {column.canSort && (
                        <span className={`ll-table__sort ${column.isSorted ? 'active' : ''}`}>
                          {column.isSorted ? (column.isSortedDesc ? '↓' : '↑') : '↕'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  onClick={() => history.push(`/${companyId}/payroll/${row.original.year}/${row.original.month}`)}
                >
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()} key={cell.column.id} className={cell.column.className || ''}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TablePager
        pageIndex={pageIndex}
        pageSize={pageSize}
        total={data.length}
        shown={page.length}
        noun="löneperioder"
        unfilteredTotal={unfilteredTotal}
        onPageChange={gotoPage}
        onPageSizeChange={setPageSize}
        sizes={[10, 25, 50]}
      />
    </div>
  );
};

export default Payrolls;
